import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import { useClubs } from '../../../hooks/clubs/useClubs'
import { usePassProducts } from '../../../hooks/passes/usePassProducts'
import { useCourtOptions } from '../../../hooks/court/useCourtOptions'

import { Card } from '../../../components/card/Card'
import { Button } from '../../../components/buttons/Button'
import Loading from '../../../components/loading/Loading'
import PageHeader from '../../../components/ui/pageHeader/PageHeader'
import { PassProductForm, emptyPassProduct } from '../../../components/authenticated/management/passProduct/PassProductForm'

import { useApp } from '../../../contexts/AppContext'
import { BannerKind } from '../../../types/contexts/AppContext'
import { PassProduct } from '../../../types/booking/booking'
import { ClubModelMetadata } from '../../../types/court/court'

export const ManagerPass = () => {
  const { passProductId } = useParams()
  const navigate = useNavigate()
  const { showBanner } = useApp()
  const { t } = useTranslation('passes')
  const { list, create, update, loading } = usePassProducts()
  const clubsHook = useClubs()
  const { courts, loaded: courtsLoaded } = useCourtOptions()
  const [clubs, setClubs] = useState<ClubModelMetadata[] | null>(null)
  const [initial, setInitial] = useState<PassProduct | null>(null)
  const isEditMode = passProductId !== undefined

  useEffect(() => {
    clubsHook.clubs(50).then(page => setClubs(page?.content ?? []))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!clubs || !courtsLoaded) {
      return
    }
    if (!isEditMode) {
      setInitial(emptyPassProduct(clubs[0], courts))
      return
    }
    list(true)
      .then(products => {
        const product = products?.find(item => String(item.id) === passProductId)
        if (!product) {
          navigate('/m/passes', { replace: true })
          return
        }
        setInitial(product)
      })
      .catch(caught => showBanner({ kind: BannerKind.ERR, msg: (caught as Error).message }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [passProductId, clubs, courtsLoaded])

  const handleSave = async (product: PassProduct) => {
    try {
      if (isEditMode) {
        await update(Number(passProductId), product)
        showBanner({ kind: BannerKind.OK, msg: t('passes.successfully-updated') })
      } else {
        await create(product)
        showBanner({ kind: BannerKind.OK, msg: t('passes.successfully-created') })
      }
      navigate('/m/passes', { replace: true })
    } catch (caught) {
      showBanner({ kind: BannerKind.ERR, msg: (caught as Error).message })
    }
  }

  return (
    <>
      <PageHeader
        title={{
          eyebrow: t('passes.manager.eyebrow'),
          h1: isEditMode ? initial?.name ?? t('passes.form.edit-title') : t('passes.form.new-title'),
        }}
        sub={t('passes.manager.form-sub')}
        actions={[
          <Button
            key="back"
            label={t('passes.form.back-btn')}
            size="md"
            variant="ghost"
            onClick={() => navigate('/m/passes')}
          />,
        ]}
      />
      {initial && clubs ? (
        <Card className="col-12">
          <PassProductForm
            key={initial.versionId ?? 'new'}
            initial={initial}
            clubs={clubs}
            courts={courts}
            isEditMode={isEditMode}
            loading={loading}
            onSave={handleSave}
            onCancel={() => navigate('/m/passes')}
          />
        </Card>
      ) : (
        <Loading variant="inline" />
      )}
    </>
  )
}
